import { TAGS } from "../constants";
import {
  Task,
  TaskView,
  TaskWithPatient,
  UpdateWaitingRoom,
  UpdateWaitingRoomPatient,
  WaitingRoomPatient,
  WaitingRoomView,
} from "../types";
import { api } from "./api";

export const tasksApi = api.injectEndpoints({
  endpoints: (builder) => ({
    //tasks
    getTaskForResource: builder.query<
      Task,
      { resourceType: string; resourceId: number }
    >({
      query: ({ resourceId, resourceType }) => ({
        url: `/api/tasks/${resourceType}/${resourceId}`,
      }),
      providesTags(_result, _error, { resourceId, resourceType }) {
        return [
          { type: TAGS.TASK_FOR_RESOURCE, id: `${resourceType}-${resourceId}` },
        ];
      },
    }),
    getTasksByPatientId: builder.query<TaskView[], number>({
      query: (patientId) => ({
        method: "GET",
        url: `/api/tasks/patient/${patientId}`,
      }),
      providesTags: (_returns, _error, args) => [
        { type: TAGS.TASKS, id: args },
      ],
    }),

    assignNextWaitingPatient: builder.mutation<WaitingRoomPatient, void>({
      query: () => ({
        url: "/api/waiting-room/next",
        method: "POST",
      }),
    }),

    getWaitingRoomPatient: builder.query({
      query: (id: number) => ({
        url: `/api/waiting-room/${id}`,
        method: "GET",
      }),
      providesTags: (_returns, _error, args) => [
        { type: TAGS.NEXT_WAITING_ROOM_PATIENT, id: args },
      ],
    }),

    updateWaitingRoomPatient: builder.mutation<
      WaitingRoomPatient,
      UpdateWaitingRoomPatient
    >({
      query: (body) => ({
        url: `/api/waiting-room/${body.id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (_returns, _error, args) => [
        { type: TAGS.NEXT_WAITING_ROOM_PATIENT, id: args.id },
      ],
    }),

    getNextTaskForPatient: builder.query<
      Task,
      { queue: string; providerId: string; patientId: number }
    >({
      query: ({ patientId, ...params }) => ({
        url: `/api/tasks/patient/${patientId}/next`,
        method: "GET",
        params,
      }),
      providesTags: [TAGS.NEXT_TASK],
    }),
    getTasks: builder.query<
      { data: { tasks: TaskView }[]; count: number },
      {
        id?: string;
        patient_first_name?: string;
        patient_last_name?: string;
        queue?: string;
        task_type?: string;
        action_taken?: string;
        from: number;
        to: number;
      }
    >({
      query: (params) => ({
        method: "GET",
        url: "/api/tasks/clinicians",
        params,
      }),
      providesTags: [TAGS.TASKS],
    }),

    getTask: builder.query<TaskWithPatient, string>({
      query: (id) => ({
        url: `/api/tasks/${id}`,
        method: "GET",
      }),
      providesTags: (_returns, _error, args) => [{ type: TAGS.TASK, id: args }],
    }),

    assignNextTask: builder.mutation<
      string,
      { queue: "Provider" | "Coordinator" }
    >({
      query: (body) => ({
        method: "POST",
        url: `/api/tasks/assign`,
        body,
        responseHandler: "text",
      }),
    }),

    updateTask: builder.mutation<Task, { id: number; body: Partial<Task> }>({
      query: ({ id, body }) => ({
        method: "PUT",
        url: `/api/tasks/${id}`,
        body: body,
      }),
      invalidatesTags: [TAGS.NEXT_TASK, TAGS.TASKS],
    }),
    getWaitingRooms: builder.query<
      { data: { waiting_room: WaitingRoomView }[]; count: number },
      {
        id?: string;
        patient_id?: string;
        patient_first_name?: string;
        patient_last_name?: string;
        assignee_first_name?: string;
        assignee_last_name?: string;
        queue?: string;
        status?: string;
        order_by?: string;
        order?: string;
        from: number;
        to: number;
      }
    >({
      query: (params) => ({
        method: "GET",
        url: "/api/waiting-room",
        params,
      }),
      providesTags: [TAGS.WAITING_ROOMS],
    }),
    getTasksForWaitingRoom: builder.query<
      TaskView[],
      { patientId: number; queue: string }
    >({
      query: ({ patientId, queue }) => ({
        method: "GET",
        url: `/api/tasks/${patientId}/waiting-room/${queue}`,
      }),
      providesTags: [TAGS.WAITING_ROOMS, TAGS.TASKS],
    }),
    bulkUpdateWaitingRooms: builder.mutation<
      WaitingRoomView,
      UpdateWaitingRoom
    >({
      query: (body) => ({
        method: "PUT",
        url: "/api/waiting-room/bulk-update",
        body,
      }),
      invalidatesTags: [TAGS.WAITING_ROOMS, TAGS.TASKS],
    }),
    createTask: builder.mutation<Task, Partial<Task>>({
      query: ({ patient_id, ...rest }) => ({
        method: "POST",
        url: `/api/tasks/${patient_id}`,
        body: rest,
      }),
    }),
  }),
});

export const {
  useGetTasksQuery,
  useUpdateTaskMutation,
  useAssignNextTaskMutation,
  useGetTaskQuery,
  useAssignNextWaitingPatientMutation,
  useGetWaitingRoomPatientQuery,
  useGetTasksByPatientIdQuery,
  useGetNextTaskForPatientQuery,
  useUpdateWaitingRoomPatientMutation,
  useGetWaitingRoomsQuery,
  useGetTasksForWaitingRoomQuery,
  useBulkUpdateWaitingRoomsMutation,
  useGetTaskForResourceQuery,
  useCreateTaskMutation,
} = tasksApi;
